import Swiper from 'swiper';
import type { SwiperOptions } from 'swiper/types';
import { Navigation } from 'swiper/modules';
import { Carousel } from 'bootstrap';

import { forceDisposeBootstrapCarousel } from '@/js/utils/dispose-bootstrap-carousel';

import 'swiper/css';

const MIN_LOOP_SLIDES = 10;

export class ClusterResidentsSwiperWidget {
  private readonly root: HTMLElement;
  private readonly source: HTMLElement;
  private readonly onMount?: () => void;
  private swiper: Swiper | null = null;
  private swiperBox: HTMLElement | null = null;

  constructor(root: HTMLElement, source: HTMLElement, onMount?: () => void) {
    this.root = root;
    this.source = source;
    this.onMount = onMount;
  }

  public init(): void {
    if (!this.source.children.length) return;

    this.destroy();
    this.detachBootstrap();
    this.mountSwiper();
    this.initSwiper();
    this.onMount?.();
  }

  public destroy(): void {
    this.swiper?.destroy(true, true);
    this.swiper = null;
    this.swiperBox?.remove();
    this.swiperBox = null;

    if (!this.root.querySelector('.carousel-inner')) {
      const inner = document.createElement('div');
      inner.className = 'carousel-inner';
      this.root.appendChild(inner);
    }
  }

  private detachBootstrap(): void {
    forceDisposeBootstrapCarousel(this.root);
    Carousel.getInstance(this.root)?.dispose();

    this.root.classList.remove('carousel', 'slide', 'carousel-fade');
    this.root.removeAttribute('data-bs-ride');
    this.root.removeAttribute('data-bs-interval');

    this.root
      .querySelectorAll<HTMLButtonElement>('.carousel-control-prev, .carousel-control-next')
      .forEach((button) => {
        button.removeAttribute('data-bs-target');
        button.removeAttribute('data-bs-slide');
        button.removeAttribute('data-bs-toggle');
      });
  }

  private getSlides(): HTMLElement[] {
    return Array.from(this.source.children) as HTMLElement[];
  }

  private getLoopSlides(): HTMLElement[] {
    const slides = this.getSlides();
    if (slides.length <= 1) return slides;

    const result = [...slides];
    while (result.length < MIN_LOOP_SLIDES) {
      result.push(slides[result.length % slides.length]!);
    }
    return result;
  }

  private mountSwiper(): void {
    this.root.querySelector('.carousel-inner')?.remove();

    const controls = this.root.querySelector<HTMLElement>('.carousel-controls');
    const title = this.root.querySelector<HTMLElement>('.edge-cards-carousel__title');
    const navigation = this.root.querySelector<HTMLElement>('.carousel-controls-inner');

    if (title) this.root.appendChild(title);
    if (navigation) this.root.appendChild(navigation);
    controls?.remove();

    const box = document.createElement('div');
    box.className = 'cluster-residents-swiper-box';

    const swiperElement = document.createElement('div');
    swiperElement.className = 'swiper cluster-residents-swiper';

    const wrapper = document.createElement('div');
    wrapper.className = 'swiper-wrapper';

    this.getLoopSlides().forEach((item) => {
      const slide = document.createElement('div');
      slide.className = 'swiper-slide';
      slide.appendChild(item.cloneNode(true));
      wrapper.appendChild(slide);
    });

    swiperElement.appendChild(wrapper);
    box.appendChild(swiperElement);
    if (navigation) {
      this.root.insertBefore(box, navigation);
    } else {
      this.root.appendChild(box);
    }
    this.swiperBox = box;
  }

  private initSwiper(): void {
    const swiperElement = this.swiperBox?.querySelector<HTMLElement>('.cluster-residents-swiper');
    if (!swiperElement) return;

    const hasNavigation = this.source.children.length > 1;
    const options: SwiperOptions = {
      modules: [Navigation],
      slidesPerView: 2.4,
      slidesPerGroup: 1,
      centeredSlides: true,
      spaceBetween: 8,
      speed: 600,
      grabCursor: true,
      watchOverflow: false,
      loop: hasNavigation,
      loopAddBlankSlides: false,
      initialSlide: hasNavigation ? 1 : 0,
      breakpoints: {
        758: {
          slidesPerView: 4.4,
          centeredSlides: true,
          spaceBetween: 8,
        },
        993: {
          slidesPerView: 'auto',
          centeredSlides: false,
          spaceBetween: 16,
        },
      },
      ...(hasNavigation && {
        navigation: {
          prevEl: this.root.querySelector<HTMLElement>('.carousel-control-prev'),
          nextEl: this.root.querySelector<HTMLElement>('.carousel-control-next'),
          addIcons: false,
        },
      }),
    };

    this.swiper = new Swiper(swiperElement, options);

    if (hasNavigation && this.swiper.params.loop) {
      this.swiper.slideToLoop(1, 0);
    }
  }
}
